import type {ExtractPropsWithoutRef} from '@/types';

import styled from '@emotion/styled';
import {useBreakpointUp} from '@travel-frontend/core';
import {
  SPACING_MEDIUM,
  SPACING_SMALL,
  Stack,
} from '@travel-frontend/ui-components';
import {forwardRef} from 'react';

import {Divider} from '@/components/Divider';
import {BadgesWrapper} from '@/components/HotelBox/components/BadgesWrapper';
import {Container} from '@/components/HotelBox/components/Container';
import {Content} from '@/components/HotelBox/components/Content';
import {HotelBoxSmall} from '@/components/HotelBox/components/HotelBoxSmall';
import {HotelDetailsWrapper} from '@/components/HotelBox/components/HotelDetailsWrapper';

type Props = {
  galleryElement: React.ReactNode;
  offerInfoElement: React.ReactNode;
  priceElement: React.ReactNode;
  badgesElement: React.ReactNode;
  ratingElement: React.ReactNode;
  hotelDetailsElement: React.ReactNode;
  buttonElement: React.ReactNode;
  summaryElement: React.ReactNode;
  favoriteElement: React.ReactNode | null;
  messageElement: React.ReactNode;
  small?: boolean;
} & ExtractPropsWithoutRef<typeof Container>;

const HotelBoxComponent = forwardRef<HTMLDivElement, Props>(
  function HotelBoxComponent(
    {
      galleryElement,
      badgesElement,
      buttonElement,
      summaryElement,
      favoriteElement,
      hotelDetailsElement,
      offerInfoElement,
      priceElement,
      ratingElement,
      messageElement,
      small,
      ...restProps
    },
    ref
  ) {
    const mdUp = useBreakpointUp('md');
    const lgUp = useBreakpointUp('lg');

    if (lgUp && !small) {
      return (
        <Container ref={ref} {...restProps}>
          {galleryElement}
          <Content data-testid="hotel-info">
            <Stack
              spacing={SPACING_MEDIUM}
              justifyContent="space-between"
              minWidth={0}
            >
              <Stack direction="row" justifyContent="space-between">
                {summaryElement}
                {favoriteElement}
              </Stack>
              {ratingElement}
              <BadgesWrapper>{badgesElement}</BadgesWrapper>
              <HotelDetailsWrapper>{hotelDetailsElement}</HotelDetailsWrapper>
            </Stack>
            <Divider category="secondary" orientation="vertical" />
            <Stack justifyContent="space-between">
              <Stack direction="column" justifyContent="space-between">
                {offerInfoElement}
              </Stack>
              {messageElement}
              <Stack spacing={SPACING_SMALL}>
                {priceElement}
                {buttonElement}
              </Stack>
            </Stack>
          </Content>
        </Container>
      );
    }

    if (mdUp && !small) {
      return (
        <Container ref={ref} {...restProps}>
          {galleryElement}
          <Content data-testid="hotel-info">
            <Stack justifyContent="space-between" spacing={0.75} minWidth={0}>
              <Stack direction="row" justifyContent="space-between">
                {summaryElement}
                {favoriteElement}
              </Stack>
              {ratingElement}
              <BadgesWrapper>{badgesElement}</BadgesWrapper>
              {hotelDetailsElement}
            </Stack>
            <Divider category="secondary" orientation="horizontal" />
            <Stack justifyContent="space-between">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="end"
              >
                {offerInfoElement}
                {priceElement}
              </Stack>
              {messageElement}
              <Stack
                spacing={SPACING_SMALL}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
              >
                {buttonElement}
              </Stack>
            </Stack>
          </Content>
        </Container>
      );
    }

    return (
      <HotelBoxSmall
        {...restProps}
        ref={ref}
        galleryElement={galleryElement}
        offerInfoElement={offerInfoElement}
        priceElement={priceElement}
        badgesElement={badgesElement}
        ratingElement={ratingElement}
        hotelDetailsElement={hotelDetailsElement}
        buttonElement={buttonElement}
        summaryElement={summaryElement}
        messageElement={messageElement}
        favoriteElement={favoriteElement}
      />
    );
  }
);

export const HotelBox = styled(HotelBoxComponent)``;
